import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Typography,
  Box,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import { Edit, Delete } from "@mui/icons-material";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import IOSSwitch from "../../Components/Buttons/IOSSwitch";
import CardHeadMenu from "../../Components/Card/CardHeadMenu/CardHeadMenu";
import configServ from "../../services/config";

const Ad = () => {
  const { company_id, user_id } = useSelector((state) => state.GlobalVariables);

  const [isCreate, setIsCreate] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [adList, setAdList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    url: "",
    about: "",
    active: true,
    photos: [],
  });

  const handlePopup = () => {
    setIsCreate(!isCreate);
    setIsEdit(false);
    clear();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const newImages = [];

    for (let file of files) {
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} exceeds 5MB limit.`);
        continue;
      }
      const base64 = await toBase64(file);
      newImages.push(base64);
    }

    const updated = [...formData.photos, ...newImages].slice(0, 10);

    setFormData((prev) => ({
      ...prev,
      photos: updated,
    }));
  };

  const handleDeleteImage = (index) => {
    const updated = formData.photos.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      photos: updated,
    }));
  };

  const clear = () => {
    setFormData({
      url: "",
      about: "",
      active: true,
      photos: [],
    });
    setIsEdit(false);
  };

  const fetchAds = async () => {
    setLoading(true);
    try {
      const res = await configServ.getAllAds(company_id);
      if (res.status) {
        setAdList(res.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (company_id) fetchAds();
  }, [company_id]);

  const handleSubmit = async () => {
    if (!formData.url || !formData.url.trim()) {
      Swal.fire("Error", "URL is required", "error");
      return;
    }

    // 🔹 URL must start with https://
    const urlPattern = /^https:\/\/[^\s/$.?#].[^\s]*$/i;

    if (!urlPattern.test(formData.url.trim())) {
      Swal.fire("Error", "URL must start with https:// and be valid", "error");
      return;
    }

    // 🔹 At least one image required
    if (!formData.photos || formData.photos.length === 0) {
      Swal.fire("Error", "Please upload at least one image", "error");
      return;
    }
    console.log("Submitting form with data:", formData);

    try {
      let res;

      if (isEdit) {
        res = await configServ.updateAd(formData.id, {
          ...formData,
          company_id,
          updated_by: user_id,
        });
      } else {
        res = await configServ.createAd({
          ...formData,
          company_id,
          created_by: user_id,
        });
      }

      if (res.status) {
        Swal.fire("Success", res.message, "success");
        clear();
        setIsCreate(false);
        fetchAds();
      }
    } catch (error) {
      Swal.fire("Error", "Something went wrong!", "error");
    }
  };

  const onEdit = (item) => {
    setIsCreate(true);
    setIsEdit(true);
    setFormData({
      ...item,
      photos: item.photos || [],
      active: item.status === 1 ? true : false,
    });
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        configServ.deleteAd(id).then(() => {
          fetchAds();
          Swal.fire("Deleted!", "Ad deleted successfully.", "success");
        });
      }
    });
  };

  return (
    <>
      <CardHeadMenu
        title={"Ad"}
        handlePopup={handlePopup}
        create={isCreate}
        isEdit={isEdit}
      />

      {isCreate && (
        <motion.div
          style={{
            width: "100%",
            backgroundColor: "#e3f3fc",
            padding: 20,
            borderRadius: "10px",
            marginTop: 10,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Ad Url"
                name="url"
                value={formData.url}
                onChange={handleChange}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Alt"
                name="about"
                value={formData.about}
                onChange={handleChange}
                multiline
                minRows={1}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={formData.active}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          active: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Active"
                />
              </FormGroup>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Upload Image
              </Typography>

              <Box display="flex" flexWrap="wrap" gap={2}>
                {formData.photos.map((img, idx) => (
                  <Box key={idx} position="relative">
                    <img
                      src={img}
                      alt={`upload-${idx}`}
                      width={100}
                      height={100}
                      style={{
                        borderRadius: 8,
                        objectFit: "cover",
                        border: "1px solid #ccc",
                      }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      sx={{
                        position: "absolute",
                        top: -10,
                        right: -10,
                        background: "white",
                      }}
                      onClick={() => handleDeleteImage(idx)}
                    >
                      ✕
                    </IconButton>
                  </Box>
                ))}

                {formData.photos.length < 1 && (
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{ width: 100, height: 100 }}
                  >
                    +
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </Button>
                )}
              </Box>
            </Grid>

            <Grid container spacing={2} justifyContent="flex-end" mt={2}>
              <Grid item>
                <Button
                  onClick={clear}
                  variant="contained"
                  color="secondary"
                  startIcon={<RotateLeftIcon />}
                >
                  Reset
                </Button>
              </Grid>
              <Grid item>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  color={isEdit ? "success" : "primary"}
                  startIcon={isEdit ? <UpdateIcon /> : <SaveIcon />}
                >
                  {isEdit ? "Update" : "Save"}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </motion.div>
      )}

      {/* ================= LOADER ================= */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : adList.length === 0 ? (
        /* ================= NO DATA ================= */
        <Box
          sx={{
            textAlign: "center",
            py: 5,
            color: "gray",
            fontWeight: 500,
          }}
        >
          No Ads Found
        </Box>
      ) : (
        <>
          {/* ================= DESKTOP TABLE ================= */}
          <Box
            sx={{
              mt: 3,
              width: "100%",
              display: { xs: "none", md: "block" },
            }}
          >
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ backgroundColor: "#1976d2" }}>
                  <TableRow>
                    <TableCell sx={{ color: "#fff" }}>S.No.</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Image</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Url</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Alt Text</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Status</TableCell>
                    <TableCell sx={{ color: "#fff" }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {adList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((ad, index) => (
                      <TableRow key={ad.id}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                        <TableCell>
                          <img
                            src={ad.photos?.[0] || "/no-image.png"}
                            alt={ad.about}
                            width={100}
                            height={100}
                            style={{
                              borderRadius: 8,
                              objectFit: "cover",
                              border: "1px solid #ccc",
                            }}
                          />
                        </TableCell>

                        <TableCell>{ad.url}</TableCell>
                        <TableCell>{ad.about}</TableCell>

                        <TableCell>
                          <Chip
                            label={ad.status ? "Active" : "Inactive"}
                            color={ad.status ? "success" : "default"}
                            size="small"
                          />
                        </TableCell>

                        <TableCell align="right">
                          <IconButton onClick={() => onEdit(ad)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* ================= MOBILE CARDS ================= */}
          <Box
            sx={{
              mt: 2,
              display: { xs: "block", md: "none" },
            }}
          >
            {adList
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((ad) => (
                <Card
                  key={ad.id}
                  sx={{
                    mb: 2,
                    borderRadius: 3,
                    boxShadow: 3,
                  }}
                >
                  <CardContent>
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                      <img
                        src={ad.photos?.[0] || "/no-image.png"}
                        alt={ad.about}
                        width="100%"
                        height={180}
                        style={{
                          borderRadius: 10,
                          objectFit: "cover",
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      <strong>URL:</strong> {ad.url}
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>About:</strong> {ad.about}
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={ad.status ? "Active" : "Inactive"}
                        color={ad.status ? "success" : "default"}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ mt: 2, textAlign: "right" }}>
                      <IconButton onClick={() => onEdit(ad)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
          </Box>
          <TablePagination
            component="div"
            count={adList.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) =>
              setRowsPerPage(parseInt(e.target.value, 10))
            }
          />
        </>
      )}
    </>
  );
};

export default Ad;
